import { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { useLenis } from "lenis/react";
import { ArrowDown } from "lucide-react";
import { hero, images, transformation } from "../content/site";
import { LineReveal } from "./Reveal";
import Hero3D, { hasWebGL } from "./Hero3D";

export default function Hero() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const [use3D, setUse3D] = useState(false);

  useEffect(() => {
    setUse3D(hasWebGL() && !reduce && window.innerWidth >= 768);
  }, [reduce]);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const contentOpacity = useTransform(scrollYProgress, [0, 0.11], [1, 0]);
  const contentY = useTransform(scrollYProgress, [0, 0.28], ["0%", reduce ? "0%" : "-14%"]);
  const scrimOpacity = useTransform(scrollYProgress, [0.17, 0.3], [1, 0]);

  // --- hero -> "chore" transition choreography (pinned, scroll-linked) ---
  // stage 2/3: tension + compression (a closing vignette, never a flat rectangle)
  const darkenOpacity = useTransform(scrollYProgress, [0.24, 0.38], [0, 1]);
  const compress = useTransform(scrollYProgress, [0.24, 0.44], reduce ? [1, 1] : [1.25, 0.86]);
  // stage 3: textured dark backdrop (soft reflections of the drum remain)
  const texturedOpacity = useTransform(scrollYProgress, [0.34, 0.44], [0, 1]);
  const texturedScale = useTransform(scrollYProgress, [0.34, 0.62], reduce ? [1, 1] : [1.05, 1.2]);
  // a blurred curved metal reflection sweeping across
  const sweepX = useTransform(scrollYProgress, [0.34, 0.5], ["-45%", "145%"]);
  const sweepOpacity = useTransform(scrollYProgress, [0.34, 0.42, 0.5], [0, 0.6, 0]);
  // stage 4: the tension statement — appears, then holds for a long beat
  const msgOpacity = useTransform(scrollYProgress, [0.4, 0.46, 0.66, 0.7], [0, 1, 1, 0]);
  const msgY = useTransform(scrollYProgress, [0.4, 0.46], [26, 0]);
  // stage 5: solid cream release — clean square wipe after the long hold
  const creamY = useTransform(scrollYProgress, [0.665, 0.705], ["100%", "0%"]);
  // fallback image forward zoom
  const fbScale = useTransform(scrollYProgress, [0, 1], reduce ? [1, 1] : [1.05, 1.7]);

  const go = (e, href) => {
    e.preventDefault();
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const lenis = useLenis();
  // begin the immersive scroll experience (don't skip to the next section)
  const startScroll = (e) => {
    e.preventDefault();
    const target = Math.round(window.innerHeight * 1.15);
    if (lenis) lenis.scrollTo(target, { duration: 2.2 });
    else window.scrollTo({ top: target, behavior: "smooth" });
  };

  return (
    <section id="top" ref={ref} className="relative h-[340vh] bg-cream" data-testid="hero-section">
      <div className="sticky top-0 h-screen w-full overflow-hidden">
        {/* soapy-drum fly-through */}
        {use3D ? (
          <div className="absolute inset-0" data-testid="hero-3d">
            <Hero3D progress={scrollYProgress} />
          </div>
        ) : (
          <motion.div className="absolute inset-0" style={{ scale: fbScale }} data-testid="hero-fallback">
            <img
              src={images.heroDrum}
              alt="Inside a washing machine drum during a soapy wash cycle"
              className="h-full w-full object-cover"
            />
          </motion.div>
        )}

        {/* legibility scrim for hero copy (fades before the transition) */}
        <motion.div
          style={{ opacity: scrimOpacity }}
          className="pointer-events-none absolute inset-0 z-[1]"
        >
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(90deg, rgba(12,16,26,0.78) 0%, rgba(12,16,26,0.45) 42%, rgba(12,16,26,0.05) 72%)",
            }}
          />
        </motion.div>

        {/* stage 2/3 — tension & compression: a closing vignette (never a flat block) */}
        <motion.div
          style={{
            opacity: darkenOpacity,
            scale: compress,
            background:
              "radial-gradient(circle at 50% 47%, rgba(18,30,47,0) 20%, rgba(18,30,47,0.72) 54%, rgba(9,11,16,0.97) 100%)",
          }}
          className="pointer-events-none absolute inset-0 z-[2]"
        />

        {/* stage 3 — textured dark backdrop: soft reflections of the drum remain */}
        {use3D ? (
          <motion.div style={{ opacity: texturedOpacity }} className="pointer-events-none absolute inset-0 z-[3] overflow-hidden">
            <motion.img
              src={images.heroDrum}
              alt=""
              aria-hidden="true"
              style={{ scale: texturedScale }}
              className="h-full w-full object-cover [filter:blur(18px)_saturate(0.85)_brightness(0.32)]"
            />
            <div className="absolute inset-0 bg-navy/70" />
            <div
              className="absolute inset-0"
              style={{ background: "radial-gradient(circle at 50% 46%, rgba(200,179,142,0.10) 0%, rgba(9,11,16,0) 45%)" }}
            />
          </motion.div>
        ) : (
          <motion.div
            style={{
              opacity: texturedOpacity,
              background:
                "radial-gradient(120% 90% at 50% 40%, rgba(26,42,66,0.9) 0%, rgba(18,30,47,0.96) 45%, rgba(9,11,16,0.99) 100%)",
            }}
            className="pointer-events-none absolute inset-0 z-[3]"
          />
        )}

        {/* stage 3 — blurred curved metal reflection sweeping across */}
        <motion.div style={{ opacity: sweepOpacity }} className="pointer-events-none absolute inset-0 z-[4] overflow-hidden">
          <motion.div
            style={{ x: sweepX }}
            className="absolute -inset-y-16 left-0 w-1/3 -rotate-12 bg-gradient-to-r from-transparent via-brass/40 to-transparent [filter:blur(30px)]"
          />
        </motion.div>

        {/* stage 4 — the tension statement, held on the textured dark frame */}
        <motion.div
          style={{ opacity: msgOpacity, y: msgY }}
          className="pointer-events-none absolute inset-0 z-[5] flex items-center justify-center px-8"
          data-testid="transition-statement"
        >
          <p className="max-w-2xl text-center font-sans text-sm font-semibold uppercase leading-relaxed tracking-[0.28em] text-cream/90 sm:text-base">
            {transformation.before}
          </p>
        </motion.div>

        {/* stage 5 — solid cream release (clean square wipe) */}
        <motion.div style={{ y: creamY }} className="absolute inset-0 z-[6] bg-cream" />

        {/* Hero copy */}
        <motion.div
          style={{ opacity: contentOpacity, y: contentY }}
          className="relative z-10 flex h-full items-center"
        >
          <div className="container-edge">
            <div className="max-w-3xl">
              <h1 className="font-serif text-5xl font-medium leading-[0.98] tracking-tight text-cream sm:text-6xl lg:text-7xl [text-shadow:0_2px_40px_rgba(0,0,0,0.4)]">
                <LineReveal lines={hero.headlineLines} delay={0.5} stagger={0.14} lineClassName="pb-1" />
              </h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.1, duration: 1 }}
                className="mt-8 max-w-xl font-sans text-base font-light leading-relaxed text-cream/90 sm:text-lg [text-shadow:0_1px_20px_rgba(0,0,0,0.5)]"
              >
                {hero.body}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.35, duration: 1 }}
                className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4"
              >
                <a
                  href={hero.ctaPrimary.href}
                  data-testid="hero-cta-primary"
                  onClick={startScroll}
                  className="group inline-flex items-center gap-3 bg-cream px-8 py-4 font-sans text-sm uppercase tracking-widest text-navy transition-colors duration-300 hover:bg-brass"
                >
                  {hero.ctaPrimary.label}
                  <ArrowDown size={16} className="transition-transform duration-300 group-hover:translate-y-1" />
                </a>
                <a
                  href={hero.ctaSecondary.href}
                  data-testid="hero-cta-secondary"
                  onClick={(e) => go(e, hero.ctaSecondary.href)}
                  className="link-underline font-sans text-sm uppercase tracking-widest text-cream"
                >
                  {hero.ctaSecondary.label}
                </a>
              </motion.div>
            </div>
          </div>
        </motion.div>

        {/* Scroll hint */}
        <motion.div style={{ opacity: contentOpacity }} className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2">
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            className="flex flex-col items-center gap-2 text-cream/80"
          >
            <span className="font-sans text-[10px] uppercase tracking-overline">{hero.scrollHint}</span>
            <ArrowDown size={16} />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
